import { AlertTriangle, PauseCircle } from "lucide-react";

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

interface StoreUnavailableProps {
  storeName: string;
  logoUrl?: string | null;
  whatsapp: string;
  colorPrimary?: string;
  colorSecondary?: string;
  isBooking?: boolean;
  /** Agenda de loja com "Loja ativa" desligada (a vitrine usa o StoreInactiveCard). */
  pausada?: boolean;
}

export function StoreUnavailable({ storeName, logoUrl, whatsapp, colorPrimary, colorSecondary, isBooking = false, pausada = false }: StoreUnavailableProps) {
  const phone = (whatsapp || "").replace(/\D/g, "");
  const msg = encodeURIComponent(
    pausada
      ? `Olá! Tentei agendar pelo link da agenda de ${storeName.trim()}, mas aparece que os agendamentos estão pausados. Gostaria de fazer um agendamento.`
      : isBooking
      ? `Olá! Tentei acessar a agenda ${storeName} pelo link, mas está temporariamente indisponível. Gostaria de fazer um agendamento.`
      : `Olá! Tentei acessar a loja ${storeName} pelo link, mas está temporariamente indisponível. Gostaria de fazer um pedido.`
  );
  const primary = colorPrimary || "#DC2626";
  const secondary = colorSecondary || "#FACC15";

  return (
    <div className="min-h-[100svh] flex items-center justify-center p-4" style={{ backgroundColor: `${primary}08` }}>
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center" style={{ borderTop: `4px solid ${secondary}` }}>
        {logoUrl && (
          <img
            src={logoUrl}
            alt={storeName}
            className="w-20 h-20 rounded-2xl object-cover mx-auto mb-4 shadow-sm"
          />
        )}
        {!logoUrl && (
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${primary}15` }}>
            {pausada ? <PauseCircle className="w-8 h-8" style={{ color: primary }} /> : <AlertTriangle className="w-8 h-8" style={{ color: primary }} />}
          </div>
        )}
        {pausada ? (
          <>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="booking-paused">Agendamentos pausados</h1>
            <p className="text-gray-500 mt-3 leading-relaxed">
              No momento, <strong>{storeName}</strong> não está recebendo agendamentos pelo link.
              Fale direto pelo WhatsApp para agendar.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-gray-900">Esta loja está indisponível no momento</h1>
            <p className="text-gray-500 mt-3 leading-relaxed">
              A loja <strong>{storeName}</strong> atingiu o limite de {isBooking ? "agendamentos" : "pedidos"} do período.
              Entre em contato diretamente pelo WhatsApp para {isBooking ? "agendar" : "continuar comprando"}.
            </p>
          </>
        )}
        {phone && <a
          href={`https://api.whatsapp.com/send?phone=${phone}&text=${msg}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 w-full py-3.5 rounded-xl font-semibold text-white flex items-center justify-center gap-2 shadow-lg transition-transform active:scale-[0.98] hover:brightness-110"
          style={{ backgroundColor: "#25D366" }}
        >
          <WhatsAppIcon className="w-5 h-5" />
          {pausada ? "Chamar no WhatsApp" : "Avisar o proprietário"}
        </a>}
      </div>
    </div>
  );
}
